import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { User } from '@/types';
import type { MFAMethod } from '@/services/mfaAPI';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // MFA state
  mfaRequired: boolean;
  mfaSetupRequired: boolean;
  mfaMethod: MFAMethod | null;
  mfaUserId: string | null;
  mfaPhoneHint: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,
  mfaRequired: false,
  mfaSetupRequired: false,
  mfaMethod: null,
  mfaUserId: null,
  mfaPhoneHint: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{
      user: User;
      token: string;
      refreshToken: string;
    }>) => {
      state.isLoading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.error = null;
      
      // Store in localStorage
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    refreshTokenSuccess: (state, action: PayloadAction<{
      token: string;
      refreshToken: string;
    }>) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;

      // Update localStorage
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    },
    // MFA reducers
    mfaChallenge: (state, action: PayloadAction<{
      userId: string;
      mfaMethod: MFAMethod;
      phoneHint?: string;
    }>) => {
      state.isLoading = false;
      state.mfaRequired = true;
      state.mfaSetupRequired = false;
      state.mfaUserId = action.payload.userId;
      state.mfaMethod = action.payload.mfaMethod;
      state.mfaPhoneHint = action.payload.phoneHint || null;
      state.error = null;
    },
    mfaSetupNeeded: (state, action: PayloadAction<{
      userId: string;
    }>) => {
      state.isLoading = false;
      state.mfaRequired = false;
      state.mfaSetupRequired = true;
      state.mfaUserId = action.payload.userId;
      state.error = null;
    },
    mfaClearTemp: (state) => {
      state.mfaRequired = false;
      state.mfaSetupRequired = false;
      state.mfaUserId = null;
      state.mfaMethod = null;
      state.mfaPhoneHint = null;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  updateUser,
  clearError,
  setLoading,
  refreshTokenSuccess,
  mfaChallenge,
  mfaSetupNeeded,
  mfaClearTemp,
} = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectIsLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectMfaRequired = (state: { auth: AuthState }) => state.auth.mfaRequired;
export const selectMfaSetupRequired = (state: { auth: AuthState }) => state.auth.mfaSetupRequired;
export const selectMfaUserId = (state: { auth: AuthState }) => state.auth.mfaUserId;
export const selectMfaMethod = (state: { auth: AuthState }) => state.auth.mfaMethod;
export const selectMfaPhoneHint = (state: { auth: AuthState }) => state.auth.mfaPhoneHint;